import { ErrorHelper, RichTextHelper } from '@helpers';
import { useCallback, useState } from 'react';

interface AssessmentWithComment {
  id: number;
  comment?: string;
}

interface AssessmentsApi {
  apiAssessmentsPartialUpdate: (params: {
    id: number;
    patchedAssessmentComment: { comment: string };
  }) => Promise<unknown>;
}

interface UseAssessmentCommentsParams {
  api: AssessmentsApi;
  updateComment: (id: number, comment: string) => void;
}

export const useAssessmentComments = ({ api, updateComment }: UseAssessmentCommentsParams) => {
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [activeAssessment, setActiveAssessment] = useState<AssessmentWithComment | null>(null);
  const [commentModalMode, setCommentModalMode] = useState<'add' | 'edit'>('add');
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [commentAction, setCommentAction] = useState<'save' | null>(null);
  const [isDeletingComment, setIsDeletingComment] = useState(false);

  const closeCommentModal = useCallback(() => {
    setCommentAction(null);
    setShowCommentModal(false);
  }, []);

  const openCommentModal = useCallback((assessment: AssessmentWithComment) => {
    setActiveAssessment(assessment);
    setCommentText(assessment.comment || '');
    setCommentModalMode(RichTextHelper.hasPlainText(assessment.comment) ? 'edit' : 'add');
    setShowCommentModal(true);
  }, []);

  const saveComment = useCallback(async () => {
    if (!activeAssessment) return;

    const nextComment = RichTextHelper.normalizeComment(commentText);

    setIsSavingComment(true);
    setCommentAction('save');
    try {
      await api.apiAssessmentsPartialUpdate({
        id: activeAssessment.id,
        patchedAssessmentComment: { comment: nextComment },
      });
      updateComment(activeAssessment.id, nextComment);
      setShowCommentModal(false);
    } catch (error) {
      ErrorHelper.handleErrorNotification(error);
    } finally {
      setIsSavingComment(false);
      setCommentAction(null);
    }
  }, [activeAssessment, api, commentText, updateComment]);

  const deleteComment = useCallback(
    async (assessment: AssessmentWithComment) => {
      setIsDeletingComment(true);
      try {
        await api.apiAssessmentsPartialUpdate({
          id: assessment.id,
          patchedAssessmentComment: { comment: '' },
        });
        updateComment(assessment.id, '');

        if (activeAssessment?.id === assessment.id) {
          setCommentText('');
          setShowCommentModal(false);
          setCommentAction(null);
        }
      } catch (error) {
        ErrorHelper.handleErrorNotification(error);
      } finally {
        setIsDeletingComment(false);
      }
    },
    [activeAssessment?.id, api, updateComment],
  );

  const isSaveDisabled = RichTextHelper.normalizeComment(commentText) === '';

  return {
    showCommentModal,
    activeAssessment,
    commentModalMode,
    commentText,
    isSaveDisabled,
    isSavingComment,
    commentAction,
    isDeletingComment,
    setCommentText,
    openCommentModal,
    closeCommentModal,
    saveComment,
    deleteComment,
  };
};
